import { PageMeta } from "@/components/PageMeta";
import { SectionHeading } from "@/components/SectionHeading";
import { GlassPanel } from "@/components/GlassPanel";
import { CtaLink } from "@/components/Button";
import { Keycap } from "@/components/keycap/Keycap";
import { routeSeo } from "@/content/seo";

const steps = [
  {
    title: "Test",
    body: "Automated tests run against the component before it is trusted anywhere else on the canvas.",
  },
  {
    title: "Validate",
    body: "Compatibility and configuration are checked against the component's declared contract.",
  },
  {
    title: "Integrity-check",
    body: "A cryptographic hash is attached to the package so a later copy can be verified against the original build. This is integrity hashing. Devory does not describe it as cryptographic signing.",
  },
  {
    title: "Version",
    body: "A qualified result is recorded against one specific, addressable version, never a moving target.",
  },
];

export function QualificationPage() {
  const seo = routeSeo["/qualification"];
  return (
    <>
      <PageMeta title={seo.title} description={seo.description} path="/qualification" />

      <section className="container page-hero">
        <p className="eyebrow">Testing and qualification</p>
        <h1 className="page-hero-title">Trust is earned before a component is reused</h1>
        <p className="page-hero-lede">
          Components are tested, validated and integrity-checked before they are trusted elsewhere
          on the canvas or published for others, described honestly at the level the platform
          actually implements today.
        </p>
      </section>

      <section className="container section-tight page-diagram">
        <Keycap id="qualify" size="xl" state="active" />
      </section>

      <section className="container section" aria-labelledby="steps-heading">
        <SectionHeading
          level={2}
          title={<span id="steps-heading">What qualification checks</span>}
        />
        <div className="grid-2">
          {steps.map((step) => (
            <GlassPanel as="article" key={step.title} className="audience-card">
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </GlassPanel>
          ))}
        </div>
      </section>

      <section className="container section-tight" aria-labelledby="qualification-limits-heading">
        <GlassPanel className="problem-card">
          <SectionHeading
            level={2}
            title={<span id="qualification-limits-heading">What qualification does not claim</span>}
          />
          <ul className="list-check">
            <li>
              Package integrity checking is a cryptographic hash, not a signature. Devory never
              calls it &ldquo;signed&rdquo;
            </li>
            <li>Security scanning of components is not a live capability today</li>
            <li>
              Execution of qualified components is currently limited to trusted, known components.
              There is no sandbox for untrusted code yet
            </li>
          </ul>
        </GlassPanel>
      </section>

      <section className="container section-tight">
        <GlassPanel className="cta-banner">
          <h2>Where qualified components go next</h2>
          <p>
            A qualified component can stay private, move to a team library, or reach the
            Marketplace.
          </p>
          <div className="cta-banner-actions">
            <CtaLink to="/marketplace" variant="secondary">
              Browse the Marketplace
            </CtaLink>
            <CtaLink to="/contact" variant="primary">
              Contact Devory
            </CtaLink>
          </div>
        </GlassPanel>
      </section>
    </>
  );
}
